import React, { useState, useEffect } from "react"
import { assignmentsApi } from "../../lib/api"
import StatusBadge from "./StatusBadge"
import DueTimeIndicator from "./DueTimeIndicator"
import GradeDisplay from "./GradeDisplay"

// Course colors for visual identity
const courseColors = [
  "bg-violet-500",
  "bg-cyan-500",
  "bg-fuchsia-500",
  "bg-orange-500",
  "bg-emerald-500",
  "bg-rose-500",
  "bg-sky-500",
  "bg-amber-500",
]

const AssignmentCard = ({ assignment, onToggleComplete, onRemove, onEdit, onDelete }) => {
  const [isCompleting, setIsCompleting] = useState(false)
  const [justCompleted, setJustCompleted] = useState(false)
  const [fadingOut, setFadingOut] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  // Get consistent color based on course ID
  const courseColor = courseColors[assignment.course.id % courseColors.length]

  // Check if this is a manual (non-synced) assignment
  const isManual = !assignment.synced

  const handleToggleComplete = async (e) => {
    e.preventDefault()
    e.stopPropagation()

    // If already manually completed, just toggle it back
    if (assignment.manually_completed) {
      try {
        setIsCompleting(true)
        await assignmentsApi.toggleComplete(assignment.id)
        onToggleComplete?.()
      } catch (error) {
        console.error("Failed to toggle completion:", error)
      } finally {
        setIsCompleting(false)
      }
      return
    }

    // Mark as completed with animation
    try {
      setIsCompleting(true)
      await assignmentsApi.toggleComplete(assignment.id)
      setJustCompleted(true)
    } catch (error) {
      console.error("Failed to toggle completion:", error)
      setIsCompleting(false)
    }
  }

  const handleEdit = (e) => {
    e.preventDefault()
    e.stopPropagation()
    onEdit?.(assignment)
  }

  const handleDeleteClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setShowDeleteConfirm(true)
  }

  const handleDeleteConfirm = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    try {
      await assignmentsApi.delete(assignment.id)
      onDelete?.(assignment.id)
    } catch (error) {
      console.error("Failed to delete assignment:", error)
    }
    setShowDeleteConfirm(false)
  }

  const handleDeleteCancel = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setShowDeleteConfirm(false)
  }

  // Handle the fade-out animation after 3 seconds
  useEffect(() => {
    if (justCompleted) {
      const fadeTimer = setTimeout(() => {
        setFadingOut(true)
      }, 3000)

      return () => clearTimeout(fadeTimer)
    }
  }, [justCompleted])

  // Remove from list after fade animation completes
  useEffect(() => {
    if (fadingOut) {
      const removeTimer = setTimeout(() => {
        onRemove?.(assignment.id)
      }, 300) // Match the CSS transition duration

      return () => clearTimeout(removeTimer)
    }
  }, [fadingOut, onRemove, assignment.id])

  // Don't show as completed if marked missing (even if graded)
  const isMissing = assignment.submission?.missing
  const isCompleted = !isMissing && (justCompleted || assignment.manually_completed || assignment.status === "submitted" || assignment.status === "graded")

  // Don't render if fully faded out
  if (fadingOut) {
    return (
      <div className="transition-all duration-300 opacity-0 max-h-0 overflow-hidden" />
    )
  }

  // Delete confirmation overlay
  if (showDeleteConfirm) {
    return (
      <div className="assignment-card block shadow-sm p-4 bg-red-50 border border-red-200">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="font-medium text-red-900">Delete "{assignment.name}"?</p>
            <p className="text-sm text-red-700 mt-1">This action cannot be undone.</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleDeleteCancel}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteConfirm}
              className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    )
  }

  const CardWrapper = assignment.html_url ? 'a' : 'div'
  const cardProps = assignment.html_url ? {
    href: assignment.html_url,
    target: "_blank",
    rel: "noopener noreferrer"
  } : {}

  return (
    <CardWrapper
      {...cardProps}
      className={`
        assignment-card block shadow-sm hover:shadow-md transition-all duration-200 p-3 sm:p-4
        ${isCompleted ? "opacity-60" : ""}
        ${justCompleted ? "bg-emerald-50 ring-1 ring-emerald-200" : "bg-white"}
        hover-lift
      `}
      style={{ '--urgency-color': justCompleted ? '#10b981' : undefined }}
    >
      <div className="flex items-start gap-2 sm:gap-3">
        {/* Completion checkbox */}
        <button
          onClick={handleToggleComplete}
          disabled={isCompleting || justCompleted}
          className={`
            mt-0.5 flex-shrink-0 w-5 h-5 rounded-full border-2 transition-all duration-200
            flex items-center justify-center
            ${assignment.manually_completed || justCompleted
              ? "bg-emerald-500 border-emerald-500 text-white check-complete"
              : "border-[var(--cream)] hover:border-emerald-400 hover:bg-emerald-50"
            }
          `}
        >
          {(assignment.manually_completed || justCompleted) && (
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          )}
        </button>

        {/* Course color indicator */}
        <div className={`w-1 self-stretch min-h-[3rem] rounded-full ${courseColor} flex-shrink-0 opacity-60`} />

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {/* Header row with name and status */}
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              {/* Assignment name */}
              <h3 className={`
                font-medium text-[var(--ink)] leading-snug text-sm sm:text-base
                completed-line ${isCompleted ? "is-completed text-[var(--ink-light)]" : ""}
              `}>
                {assignment.name}
                {isManual && (
                  <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                    Manual
                  </span>
                )}
              </h3>

              {/* Course name */}
              <p className="text-xs sm:text-sm text-[var(--ink-light)] mt-0.5 truncate">
                {assignment.course.name}
              </p>
            </div>

            {/* Status badge only - grade moves to meta row on mobile */}
            <div className="flex-shrink-0">
              <StatusBadge status={assignment.status} submission={assignment.submission} />
            </div>
          </div>

          {/* Meta row - wraps on mobile */}
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs sm:text-sm">
            <DueTimeIndicator
              dueAt={assignment.due_at}
              urgency={assignment.urgency}
              submission={assignment.submission}
              isSubmitted={assignment.status === "submitted" || assignment.status === "graded" || assignment.status === "excused"}
            />

            {assignment.points_possible != null && (
              <span className="text-[var(--ink-light)] flex items-center gap-1">
                <svg className="w-3.5 h-3.5 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                {assignment.points_possible} pts
              </span>
            )}

            {assignment.submission?.score != null && (
              <GradeDisplay
                score={assignment.submission.score}
                pointsPossible={assignment.points_possible}
                percentage={assignment.submission.percentage}
                grade={assignment.submission.grade}
              />
            )}

            {assignment.submission?.late && (
              <span className="badge badge-late">Late</span>
            )}

            {assignment.submission?.missing && (
              <span className="badge badge-missing">Missing</span>
            )}

            {/* Edit/Delete buttons for manual assignments */}
            {isManual && onEdit && onDelete && (
              <div className="ml-auto flex items-center gap-1">
                <button
                  onClick={handleEdit}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Edit assignment"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
                <button
                  onClick={handleDeleteClick}
                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete assignment"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* External link icon - only show if html_url exists */}
        {assignment.html_url && (
          <svg className="flex-shrink-0 w-4 h-4 text-[var(--ink-light)] mt-1 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        )}
      </div>
    </CardWrapper>
  )
}

export default AssignmentCard
